<template>
    <div class="block-editor ai-call">
        <div class="wrapper">
            <section>
                <div class="title">Agent ID</div>
                <div class="mode-switch">
                    <a-radio-group v-model="agentIdMode" button-style="solid" size="small">
                        <a-radio-button value="static">Statik</a-radio-button>
                        <a-radio-button value="dynamic">Dinamik</a-radio-button>
                    </a-radio-group>
                </div>
                <div v-if="agentIdMode === 'static'" class="dropdown-wrapper">
                    <a-select
                        v-model="clone.agentId"
                        placeholder="Agent seçiniz"
                        style="width: 100%"
                        :loading="loadingAgents"
                        @focus="loadAgents"
                        allowClear
                        show-search
                        :filter-option="filterOption"
                    >
                        <a-select-option v-for="agent in agents" :key="agent.agent_id" :value="agent.agent_id">
                            {{ agent.agent_name }}
                        </a-select-option>
                    </a-select>
                </div>
                <div v-else class="text-editor-wrapper">
                    <TiptapEditor
                        placeholder="Agent ID girin veya attribute kullanın"
                        :show-attributes="true"
                        :show-emoji="false"
                        :max-character-count="100"
                        v-model="clone.agentId"
                    />
                </div>
            </section>
            <section>
                <div class="title">Arayan Numara</div>
                <div class="mode-switch">
                    <a-radio-group v-model="fromNumberMode" button-style="solid" size="small">
                        <a-radio-button value="static">Statik</a-radio-button>
                        <a-radio-button value="dynamic">Dinamik</a-radio-button>
                    </a-radio-group>
                </div>
                <div v-if="fromNumberMode === 'static'" class="dropdown-wrapper">
                    <a-select
                        v-model="clone.fromNumber"
                        placeholder="Arayan numara seçiniz"
                        style="width: 100%"
                        :loading="loadingNumbers"
                        @focus="loadNumbers"
                        allowClear
                        show-search
                        :filter-option="filterOption"
                    >
                        <a-select-option
                            v-for="number in phoneNumbers"
                            :key="number.phone_number"
                            :value="number.phone_number"
                        >
                            {{ number.nickname || number.phone_number_pretty || number.phone_number }}
                        </a-select-option>
                    </a-select>
                </div>
                <div v-else class="text-editor-wrapper">
                    <TiptapEditor
                        placeholder="E.164 formatında arayan numara girin (örn: +905551234567) veya attribute kullanın"
                        :show-attributes="true"
                        :show-emoji="false"
                        :max-character-count="50"
                        v-model="clone.fromNumber"
                    />
                </div>
            </section>
            <section>
                <div class="title">Aranan Numara</div>
                <div class="text-editor-wrapper">
                    <TiptapEditor
                        placeholder="E.164 formatında aranan numara girin (örn: +905551234567) veya attribute kullanın"
                        :show-attributes="true"
                        :show-emoji="false"
                        :max-character-count="50"
                        v-model="clone.toNumber"
                    />
                </div>
            </section>
            <section>
                <div class="title">Çağrı Bitene Kadar Bekle</div>
                <a-switch v-model="clone.waitForCallEnd" />
            </section>
            <section>
                <div class="title">Değişkenler</div>
                <div class="variables-container">
                    <a-form layout="vertical">
                        <a-form-item
                            v-for="(variable, index) in variableList"
                            :key="index"
                            :label="variable.key ? variable.key : 'Değişken ' + (index + 1)"
                        >
                            <div class="variable-row">
                                <div class="variable-key">
                                    <a-input
                                        v-model="variable.key"
                                        placeholder="Değişken adı"
                                        @change="updateVariables"
                                    />
                                </div>
                                <div class="variable-value">
                                    <TiptapEditor
                                        :placeholder="'Değişken değeri veya attribute'"
                                        :show-attributes="true"
                                        :show-emoji="false"
                                        :max-character-count="200"
                                        v-model="variable.value"
                                        @input="updateVariables"
                                    />
                                </div>
                                <div class="variable-actions">
                                    <a-button
                                        type="danger"
                                        shape="circle"
                                        icon="delete"
                                        @click="removeVariable(index)"
                                    />
                                </div>
                            </div>
                        </a-form-item>
                    </a-form>
                    <div class="add-variable">
                        <a-button type="dashed" @click="addVariable" block>
                            <a-icon type="plus" /> Değişken Ekle
                        </a-button>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../common/TiptapEditor";
import { VOICE_AGENT_API } from "../../../../../../api/voiceAgent";

const log = log2("AICall");
export default {
    name: "AICall",
    components: { TiptapEditor },
    props: ["value"],
    data() {
        const clone = _.cloneDeep(this.value || {});
        if (!Object.hasOwn(clone, "waitForCallEnd")) {
            clone.waitForCallEnd = false;
        }
        return {
            clone: clone,
            variableList: [],
            agentIdMode: "static",
            fromNumberMode: "static",
            agents: [],
            phoneNumbers: [],
            loadingAgents: false,
            loadingNumbers: false,
        };
    },
    created() {
        // Payload'dan değişkenleri alıp listeye dönüştür
        this.initializeVariables();
        this.initializeModes();
    },
    mounted() {
        // Agent ve numara listelerini yükle
        this.loadAgents();
        this.loadNumbers();
    },
    methods: {
        initializeVariables() {
            if (this.clone.variables && Array.isArray(this.clone.variables)) {
                this.variableList = _.cloneDeep(this.clone.variables);
            } else if (this.clone.variables && typeof this.clone.variables === "object" && !Array.isArray(this.clone.variables)) {
                // Eski yapıdan yeni yapıya geçiş için uyumluluk kodu
                this.variableList = Object.keys(this.clone.variables).map((key) => ({
                    key,
                    value: this.clone.variables[key],
                }));
                // Yeni yapıya geçir
                this.clone.variables = this.variableList;
            } else {
                this.clone.variables = [];
                this.variableList = [];
            }
        },
        initializeModes() {
            // Değerlere bakarak modları belirle
            if (this.clone.agentId && (this.clone.agentId.includes("{{") || this.clone.agentId.includes("}}"))) {
                this.agentIdMode = "dynamic";
            }

            if (
                this.clone.fromNumber &&
                (this.clone.fromNumber.includes("{{") || this.clone.fromNumber.includes("}}"))
            ) {
                this.fromNumberMode = "dynamic";
            }
        },
        async loadAgents() {
            if (this.agents.length > 0) return;

            this.loadingAgents = true;
            try {
                const response = await VOICE_AGENT_API.agents.list();
                this.agents = response || [];
            } catch (error) {
                console.error("Agent listesi yüklenirken hata oluştu:", error);
                this.$message.error("Agent listesi yüklenemedi");
            } finally {
                this.loadingAgents = false;
            }
        },
        async loadNumbers() {
            if (this.phoneNumbers.length > 0) return;

            this.loadingNumbers = true;
            try {
                const response = await VOICE_AGENT_API.numbers.list();
                this.phoneNumbers = response || [];
            } catch (error) {
                console.error("Numara listesi yüklenirken hata oluştu:", error);
                this.$message.error("Numara listesi yüklenemedi");
            } finally {
                this.loadingNumbers = false;
            }
        },
        filterOption(input, option) {
            const text = option.componentOptions.children[0].text.toLowerCase();
            return text.includes(input.toLowerCase());
        },
        addVariable() {
            this.variableList.push({
                key: "",
                value: "",
            });
            this.updateVariables();
        },
        removeVariable(index) {
            this.variableList.splice(index, 1);
            this.updateVariables();
        },
        updateVariables() {
            // Boş key'e sahip değişkenleri filtrele
            const filteredVariables = this.variableList.filter(item => !!item.key);

            // Clone nesnesini doğrudan güncelle
            this.clone.variables = filteredVariables;
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
        value: {
            deep: true,
            handler(newValue) {
                if (!_.isEqual(newValue, this.clone)) {
                    this.clone = _.cloneDeep(newValue || {});
                    this.initializeVariables();
                    this.initializeModes();
                }
            },
        },
        // Mod değiştiğinde değerleri temizle
        agentIdMode(newMode) {
            if (newMode === "static") {
                if (this.clone.agentId && (this.clone.agentId.includes("{{") || this.clone.agentId.includes("}}"))) {
                    this.clone.agentId = "";
                }
            }
        },
        fromNumberMode(newMode) {
            if (newMode === "static") {
                if (
                    this.clone.fromNumber &&
                    (this.clone.fromNumber.includes("{{") || this.clone.fromNumber.includes("}}"))
                ) {
                    this.clone.fromNumber = "";
                }
            }
        },
    },
};
</script>

<style lang="scss">
.block-editor.ai-call {
    section {
        margin-bottom: 20px;
    }

    .wrapper {
        padding: 30px;

        .title {
            margin-bottom: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .mode-switch {
            margin-bottom: 10px;
            display: flex;
            justify-content: flex-end;
        }

        .text-editor-wrapper {
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }
        }

        .dropdown-wrapper {
            margin-bottom: 5px;
        }

        .variables-container {
            background-color: #f5f5f5;
            padding: 15px;
            border-radius: 4px;

            .variable-row {
                display: flex;
                align-items: flex-start;
                gap: 10px;

                .variable-key {
                    flex: 0 0 150px;
                }

                .variable-value {
                    flex: 1;

                    .text-editor-wrapper {
                        min-height: 32px;
                    }
                }

                .variable-actions {
                    flex: 0 0 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }

            .add-variable {
                margin-top: 15px;
            }
        }
    }
}
</style>
